import React from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Box,
    Divider
} from '@mui/material'
import { QRCodeCanvas } from 'qrcode.react'

const TermAndConditionModal = ({ open, onClose, device, onAcceptTerms }) => {
    if (!device) return null

    const InfoRow = ({ label, value }) => (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography color="text.secondary" minWidth={140}>
                {label}
            </Typography>
            <Typography>{value || 'N/A'}</Typography>
        </Stack>
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle fontWeight="bold"> Corporate Device and Peripheral Care Policy</DialogTitle>

            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Intro */}
                <Typography mb={2}>
                    You have been assigned a company device. To complete the assignment, please review the details below
                    and accept the Terms and Conditions.
                </Typography>

                {/* Device Info */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} mb={3}>
                    <Box display="flex" justifyContent="center">
                        <QRCodeCanvas value={device.id || 'N/A'} size={160} level="H" />
                    </Box>

                    <Stack spacing={1} flex={1}>
                        <InfoRow label="Category:" value={device.category} />
                        <InfoRow label="Brand:" value={device.brand} />
                        <InfoRow label="Model:" value={device.model} />
                        <InfoRow label="Serial No:" value={device.serialNumber} />
                    </Stack>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {/* Policy Sections */}
                <Stack spacing={2} pl={1}>
                    <Typography mb={2}>
                        This policy outlines employee responsibilities for the proper use, care, and protection of
                        company-issued devices and peripherals to ensure data security, device longevity, and business
                        continuity.
                    </Typography>
                    <Typography fontWeight="bold">1. Purpose</Typography>
                    <Typography>
                        This policy defines employee responsibilities for the proper use, care, and protection of
                        company-issued devices and peripherals, including laptops, keyboards, mice, monitors, and
                        related accessories, to ensure data security, device longevity, and business continuity.
                    </Typography>

                    <Typography fontWeight="bold">2. Scope</Typography>
                    <Typography>
                        This policy applies to all employees, contractors, and consultants who are issued any
                        company-owned devices or peripherals.
                    </Typography>

                    <Typography fontWeight="bold">3. Ownership & Responsibility</Typography>
                    <Typography>• All devices and peripherals remain the property of the company.</Typography>

                    <Typography>
                        • Employees are responsible for the devices assigned to them and must exercise reasonable care
                        to prevent loss, theft, or damage.
                    </Typography>

                    <Typography>
                        • Devices should be used primarily for business purposes unless otherwise authorized.
                    </Typography>

                    <Typography fontWeight="bold">4. Physical Care</Typography>

                    <Typography>
                        • Handle devices carefully and use protective cases or bags when transporting laptops or
                        sensitive equipment.
                    </Typography>

                    <Typography>
                        • Keep devices away from liquids, food, dust, extreme temperatures, and direct sunlight.
                    </Typography>

                    <Typography>• Do not attempt unauthorized repairs, upgrades, or hardware modifications.</Typography>

                    <Typography>• Do not affix anything to the surface of devices or peripherals.</Typography>

                    <Typography fontWeight="bold">5. Security & Acceptable Use</Typography>
                    <Typography>
                        • Do not share login credentials or allow unauthorized individuals to use company devices.
                    </Typography>

                    <Typography>
                        • Lock screens or log out when devices are unattended. Install only company-approved software or
                        applications.
                    </Typography>

                    <Typography>• Use secure networks and VPNs when working remotely.</Typography>

                    <Typography fontWeight="bold">6. Data Protection</Typography>
                    <Typography>
                        • Store company data only on approved devices, cloud platforms, or company servers.
                    </Typography>
                    <Typography>
                        • Do not transfer or store company data on personal devices or unapproved storage media.
                    </Typography>

                    <Typography>• Follow company policies on data confidentiality and classification.</Typography>

                    <Typography fontWeight="bold">7. Maintenance & Updates</Typography>
                    <Typography>
                        • IT support shall ensure operating systems, security software, and firmware updates are kept
                        current.
                    </Typography>

                    <Typography>
                        • Please do not attempt to update systems without the IT support’s knowledge.
                    </Typography>

                    <Typography>• Report technical issues promptly to IT support for resolution.</Typography>

                    <Typography fontWeight="bold">8. Loss, Theft, or Damage</Typography>
                    <Typography>
                        • Report any device loss, theft, or damage immediately to IT and ASC Management. •
                    </Typography>

                    <Typography>
                        • Employees may be held financially responsible for damage caused by negligence or misuse, as
                        defined in company policy.
                    </Typography>

                    <Typography fontWeight="bold">9. Travel & Remote Work</Typography>
                    <Typography>• Do not leave devices unattended in public spaces or vehicles.</Typography>

                    <Typography>
                        • Follow company and client security requirements when working remotely or traveling.
                    </Typography>

                    <Typography fontWeight="bold">10. Return of Equipment</Typography>
                    <Typography>
                        All company-issued devices and peripherals must be returned in good working condition upon
                        request, role change, or termination of employment.
                    </Typography>
                </Stack>

                {/* Acceptance Notice */}
                <Typography mt={3} color="text.secondary">
                    By clicking <strong>Accept</strong>, you confirm that you have read, understood, and agreed to the
                    Corporate Device and Peripheral Care Policy.
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>

                {!device.isTermsAccepted && (
                    <Button variant="contained" color="primary" onClick={() => onAcceptTerms(device.id)}>
                        Accept Terms and Conditions
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default TermAndConditionModal
